'use client'

import { useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { TimezoneSelect } from '@/components/ui/timezone-select'
import { useAuth } from '@/hooks/use-auth'
import type { TimeCheckData, Schedule } from '@/lib/pbx/flow/types'
import { useTranslations } from "next-intl"


interface TimeCheckPanelProps {
  data: TimeCheckData
  onUpdate: (data: Partial<TimeCheckData>) => void
}

function getDAYS(tUi: ReturnType<typeof useTranslations<"Pbx.PbxFlowEditorPanelsTimeCheckPanel">>) {
  return [
  { value: 'Mon', label: tUi("monday") },
  { value: 'Tue', label: tUi("tuesday") },
  { value: 'Wed', label: tUi("wednesday") },
  { value: 'Thu', label: tUi("thursday") },
  { value: 'Fri', label: tUi("friday") },
  { value: 'Sat', label: tUi("saturday") },
  { value: 'Sun', label: tUi("sunday") },
]
}

export function TimeCheckPanel({ data, onUpdate }: TimeCheckPanelProps) {
  const tUi = useTranslations("Pbx.PbxFlowEditorPanelsTimeCheckPanel")
  const DAYS = getDAYS(tUi)


  const { user } = useAuth()
  const userTimezone = (user as { timezone?: string } | null)?.timezone || 'UTC'

  // Set user's timezone as default when node is first created (no timezone set)
  useEffect(() => {
    if (!data.timezone && userTimezone) {
      onUpdate({ timezone: userTimezone })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const schedule = data.schedule || {
    default: {
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      time_ranges: [{ start: '09:00', end: '18:00' }],
    },
    overrides: {},
  }

  const updateSchedule = (newSchedule: Partial<Schedule>) => {
    onUpdate({
      schedule: { ...schedule, ...newSchedule },
    })
  }

  const toggleDay = (day: string) => {
    const currentDays = schedule.default.days
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day]
    updateSchedule({
      default: { ...schedule.default, days: newDays },
    })
  }

  const updateTimeRange = (index: number, field: 'start' | 'end', value: string) => {
    const newRanges = [...schedule.default.time_ranges]
    newRanges[index] = { ...newRanges[index], [field]: value }
    updateSchedule({
      default: { ...schedule.default, time_ranges: newRanges },
    })
  }

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label><span key={tUi("timezone")}>{tUi("timezone")}</span></Label>
        <TimezoneSelect
          value={data.timezone || userTimezone}
          onChange={(value) => onUpdate({ timezone: value })}
        />
      </div>

      <div className="space-y-2">
        <Label><span key={tUi("workingDays")}>{tUi("workingDays")}</span></Label>
        <div className="grid grid-cols-2 gap-2">
          {DAYS.map((day) => (
            <div key={day.value} className="flex items-center space-x-2">
              <Checkbox
                id={`day-${day.value}`}
                checked={schedule.default.days.includes(day.value)}
                onCheckedChange={() => toggleDay(day.value)}
              />
              <label
                htmlFor={`day-${day.value}`}
                className="text-sm cursor-pointer"
              >
                {day.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label><span key={tUi("workingHours")}>{tUi("workingHours")}</span></Label>
        {schedule.default.time_ranges.map((range, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="time"
              value={range.start}
              onChange={(e) => updateTimeRange(index, 'start', e.target.value)}
              className="w-28"
            />
            <span className="text-muted-foreground"><span key={tUi("to")}>{tUi("to")}</span></span>
            <Input
              type="time"
              value={range.end}
              onChange={(e) => updateTimeRange(index, 'end', e.target.value)}
              className="w-28"
            />
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          <span key={tUi("forOvernightSchedulesEG220006")}>{tUi("forOvernightSchedulesEG220006")}</span></p>
      </div>

      <div className="p-3 bg-muted rounded-lg text-sm">
        <p className="text-muted-foreground">
          <span key={tUi("routeOpenToOnePathAndClosedTo")}>{tUi("routeOpenToOnePathAndClosedTo")}</span></p>
      </div>
    </div>
  )
}
